import { useState, useRef, useEffect } from "react"
import {
  TextField,
  Tooltip,
  CircularProgress,
  ClickAwayListener,
  Autocomplete,
} from "@mui/material"
import { createFilterOptions } from "@mui/material/Autocomplete"
import { IMaskInput } from "react-imask"
import { getContactEnterprises, createEnterprise } from "../../services/scout"
import { getStoredUser } from "../../services/auth"
import EditIcon from "@mui/icons-material/Edit"
import dayjs from "dayjs"
import { isValidCPF, isValidEmail, isValidBirthday } from "../../utils"

const dateSettings = { day: "2-digit", month: "2-digit", year: "numeric" }

const filter = createFilterOptions()

export default function EditableField({
  label,
  value,
  placeholder = "Clique para adicionar",
  onSave,
  type = "text",
  mask = null,
  className = "",
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [internalValue, setInternalValue] = useState(value || "") // stores id or value
  const [inputValue, setInputValue] = useState(label || value || "") // display text
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [options, setOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  const wrapperRef = useRef(null)

  // Load enterprise options
  useEffect(() => {
    if (isEditing && type === "enterprise") {
      const fetchEnterprises = async () => {
        try {
          setLoadingOptions(true)
          const enterpriseId = getStoredUser().profile.enterprise?.id
          const data = await getContactEnterprises(enterpriseId)
          setOptions(data.results || data)
        } catch (err) {
          console.error("Failed to fetch enterprises", err)
        } finally {
          setLoadingOptions(false)
        }
      }
      fetchEnterprises()
    }
  }, [isEditing, type])

  // Reset values when editing mode closes
  useEffect(() => {
    if (!isEditing) {
      if (type === "date") {
        const formatted = value ? dayjs(value).format("YYYY-MM-DD") : ""
        setInternalValue(formatted)
        setInputValue(formatted)
      } else {
        setInternalValue(value || "")
        setInputValue(label || value || "")
      }
      setError(null)
    }
  }, [value, label, isEditing, type])

  const validate = (val) => {
    const normalized = typeof val === "string" ? val.trim() : val

    if (!normalized) return null

    if (type === "email" && !isValidEmail(normalized)) {
      return "E-mail inválido"
    }

    if (type === "cpf" && !isValidCPF(normalized)) {
      return "CPF inválido"
    }

    if (type === "date" && !isValidBirthday(normalized)) {
      return "Data de aniversário inválida"
    }

    return null
  }

  const handleSave = async () => {
    let valueToSave = internalValue

    if (type !== "enterprise") {
      const trimmed = typeof internalValue === "string" ? internalValue.trim() : internalValue
      const validationError = validate(trimmed)

      if (validationError) {
        setError(validationError)
        return
      }

      valueToSave = type === "date" && trimmed === "" ? null : trimmed

      if (type === "date") {
        const current = value ? dayjs(value).format("YYYY-MM-DD") : ""
        if ((trimmed || "") === current) {
          setIsEditing(false)
          return
        }
      } else {
        const normalizedTrimmed = trimmed ?? ""
        const normalizedValue = value ?? ""
        if (normalizedTrimmed === normalizedValue) {
          setIsEditing(false)
          return
        }
      }
    }

    try {
      setIsSaving(true)
      setError(null)

      if (type !== "enterprise") {
        await onSave(valueToSave)
      }

      setIsEditing(false)
    } catch (err) {
      console.error("EditableField save failed:", err)
      setError("Erro ao salvar")
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (type === "date") {
      const formatted = value ? dayjs(value).format("YYYY-MM-DD") : ""
      setInternalValue(formatted)
      setInputValue(formatted)
    } else {
      setInternalValue(value || "")
      setInputValue(label || value || "")
    }
    setError(null)
    setIsEditing(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave()
    if (e.key === "Escape") handleCancel()
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (isEditing) handleSave()
      }}
    >
      <div
        ref={wrapperRef}
        className={`editable-field${isEditing ? " editing" : ""}${className ? ` ${className}` : ""}`}
        onClick={(e) => {
          if (isEditing) {
            e.stopPropagation()
            return
          }
          setIsEditing(true)
        }}
      >
        <div className="editable-field__value">
          {isEditing ? (
            <Tooltip open={!!error} title={error || ""} placement="top" arrow>
              {type === "enterprise" ? (
                <Autocomplete
                  autoHighlight
                  freeSolo
                  filterOptions={(opts, params) => {
                    const filtered = filter(opts, params)
                    const typedName = params.inputValue.trim()

                    // If inputValue does not match any existing option, show exactly what will be created.
                    const isExisting = opts.some(
                      o => o.name.toLowerCase() === typedName.toLowerCase()
                    )
                    if (typedName !== "" && !isExisting) {
                      filtered.push({
                        id: "__create__",
                        inputValue: typedName,
                        name: `Criar nova empresa: ${typedName}`,
                      })
                    }

                    return filtered
                  }}
                  options={options}
                  getOptionLabel={option => {
                    if (typeof option === "string") return option
                    return option.name || ""
                  }}
                  value={
                    internalValue
                      ? options.find(o => o.id === internalValue) || { name: inputValue }
                      : null
                  }
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                  onChange={async (event, newValue) => {
                    if (!newValue) {
                      setInternalValue("")
                      setInputValue("")
                      await onSave(null, null)
                      setIsEditing(false)
                      return
                    }

                    // check if nothing changed
                    if (newValue && newValue.id === value?.id && newValue.name === label) {
                      setIsEditing(false)
                      return // nothing changed, skip save
                    }

                    if (newValue.id === "__create__") {
                      const enterpriseId = getStoredUser().profile.enterprise?.id
                      const created = await createEnterprise(enterpriseId, newValue.inputValue)
                      setInternalValue(created.id)
                      setInputValue(created.name)
                      await onSave(created.id, created.name)
                    } else if (typeof newValue === "string") {
                      // fallback if the user typed and pressed Enter without selecting
                      const enterpriseId = getStoredUser().profile.enterprise?.id
                      const created = await createEnterprise(enterpriseId, newValue.trim())
                      setInternalValue(created.id)
                      setInputValue(created.name)
                      await onSave(created.id, created.name)
                    } else {
                      setInternalValue(newValue.id)
                      setInputValue(newValue.name)
                      await onSave(newValue.id, newValue.name)
                    }
                    setIsEditing(false)
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      autoFocus
                      sx={{ width: 200, fontStyle: "normal" }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: loadingOptions ? (
                          <CircularProgress size={16} />
                        ) : (
                          params.InputProps.endAdornment
                        ),
                      }}
                    />
                  )}
                  fullWidth
                />
              ) : mask ? (
                <TextField
                  autoFocus
                  size="small"
                  value={internalValue}
                  error={!!error}
                  onChange={(e) => {
                    const val = e.target.value
                    setInternalValue(val)
                    setError(validate(val))
                  }}
                  onKeyDown={handleKeyDown}
                  sx={{ width: 200, fontStyle: "normal" }}
                  InputProps={{
                    inputComponent: IMaskInput,
                    inputProps: { mask: "000.000.000-00", unmask: false },
                  }}
                  fullWidth
                />
              ) : (
                <TextField
                  autoFocus
                  size="small"
                  type={type}
                  value={internalValue}
                  disabled={isSaving}
                  error={!!error}
                  onChange={(e) => {
                    const val = e.target.value
                    setInternalValue(val)
                    setError(validate(val.trim()))
                  }}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  sx={{ width: 200, fontStyle: "normal" }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Tooltip>
          ) : (
            <>
              <EditIcon className="editable-field__edit-icon" fontSize="small" />
              <span className={!inputValue ? "empty" : ""}>
                {type === "date"
                  ? value
                    ? new Date(value).toLocaleDateString(undefined, dateSettings)
                    : placeholder
                  : inputValue || placeholder}
              </span>
            </>
          )}
        </div>
      </div>
    </ClickAwayListener>
  )
}